import { errorInstanceFormatting } from '../../../common/util/error'
import { logSentryException } from '../../../common/util/sentry'

const isProcessPaymentRequest = url => url.includes('/ProcessPayment/')

const getProcessPaymentLogData = (requestData, timeout) => {
  const { objectData = {} } = requestData
  const { paymentForm = {}, ...requestInfo } = objectData
  const allowedRequestInfo = [
    'language',
    'remoteId',
    'clientVersion',
    'device',
    'wsUser',
    'partialPaymentsAllowed',
    'paymentMethodCloseButton',
    'skipPaymentTicket',
    'displayMode',
    'fingerPrintId',
    'kryptonCallbackUrl',
    'refererUrl',
    'publicKey'
  ]
  const safeRequestInfo = allowedRequestInfo.reduce((info, key) => {
    if (key in requestInfo) info[key] = requestInfo[key]
    return info
  }, {})

  return {
    ...safeRequestInfo,
    paymentForm: {
      brand: paymentForm.brand,
      bin: paymentForm.pan?.slice(0, 6),
      paymentMethodToken: paymentForm.paymentMethodToken,
      installmentNumber: paymentForm.installmentNumber
    },
    timeout
  }
}

/**
 * @access public
 * @desc Class to handle web requests through native window "fetch" method
 */
export default class BrowserRequests {
  constructor({ $store, browser, storeFactory, logger }) {
    this.$store = $store
    this.browser = browser
    this.storeFactory = storeFactory
    this.logger = logger
  }

  /**
   * Performs a POST request on the given url or specific GET request for JSONP
   *
   * @public
   * @example
   * const testObj = {
   *   a: 1,
   *   b: 2
   * };
   * requests.post('http://172.17.0.1:7000', testObj).then((response) => {
   *     assert.ok(_.isEqual(response.toString(), testObj.toString()));
   *     done();
   * }, (error) => _.noop);
   * @param requestData
   * @param {{returnResponseOnError?: boolean}} options
   * @return {Promise} Promised response
   */
  async post(requestData, options = {}) {
    const { returnResponseOnError } = options
    const { url, objectData, headers } = requestData
    let { timeout } = requestData
    if (!timeout) timeout = this.$store.state._internals.serverRequestTimeout

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)
      const fetchData = {
        method: 'POST',
        body: JSON.stringify(objectData),
        signal: controller.signal
      }
      if (headers) fetchData.headers = headers

      this.logger.logRequest(requestData, url)
      const response = await window.fetch(url, fetchData)
      clearTimeout(timeoutId)

      // Headers
      let responseHeaders = {}
      if (response.headers.forEach) {
        response.headers.forEach((key, value) => {
          responseHeaders[value] = key
        })
      } else {
        for (const key of response.headers.keys()) {
          responseHeaders[key] = response.headers.get(key)
        }
      }

      // Body
      if (response.status !== 200) {
        if (response.status >= 500) {
          const additionalInfo = returnResponseOnError ? { response } : {}
          return Promise.reject({ errorCode: 'CLIENT_104', ...additionalInfo })
        }
        const error = new Error(response.statusText)
        error.response = response
        throw error
      }

      const data = await response.json()
      const requestResponse = this.storeFactory.create('requestResponse', {
        response: data,
        statusCode: 200,
        timestamp: new Date().getTime(),
        headers: responseHeaders
      })
      this.logger.logResponse(requestResponse, url)
      return Promise.resolve(requestResponse)
    } catch (error) {
      // temp log see KJS-6446
      if (error.name === 'AbortError' && isProcessPaymentRequest(url)) {
        logSentryException(
          'ProcessPayment request aborted',
          'BrowserRequests.js',
          getProcessPaymentLogData(requestData, timeout)
        )
      }
      error.source = 'BrowserRequests'
      const formatedError = errorInstanceFormatting(error)
      const additionalInfo = returnResponseOnError
        ? { response: error?.response }
        : {}
      return Promise.reject({ ...formatedError, ...additionalInfo })
    }
  }
}
